import { useDeferredValue, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { api, qs } from '../lib/api.js';
import type { Me, Rights } from '../lib/session.js';
import {
  DATE_STATUS_LABEL,
  REVIEW_STATUS_LABEL,
  SALES_STATUS_LABEL,
  TICKET_STATUS_LABEL,
  TRIP_STATE_LABEL,
  label,
} from '../lib/labels.js';
import { date, dateTime, money, num } from '../lib/format.js';
import {
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  PageHeader,
  Panel,
  StatusBadge,
  TabBar,
} from '../components/ui.js';
import '../screens-2a.css';
import { hrefFor } from '../lib/router.js';
import { RaceLanguages } from './RaceLanguages.js';
import { RacePotential, RaceRecommendations } from './RacePotential.js';

interface RaceListItem {
  raceId: string;
  name: string;
  series: string;
  seasonLabel: string;
  dateStart: string | null;
  dateStatus: string;
  salesStatus: string;
  promiseBy: string | null;
  racePackReady: boolean;
  tripsActive: number;
  tripsTotal: number;
  ticketsDeliveredPct: number | null;
  reviewStatus: string | null;
  fieldOwner: { date: string; sales_status: string; flexi: string };
  phase: Phase;
  effectiveSalesStatus: string;
  stats: RaceStats | null;
}

type Phase = 'past' | 'ongoing' | 'upcoming' | 'undated';
interface RaceStats {
  orders: number;
  ordersCancelled: number;
  cancelledShare: number | null;
  tickets: number;
  customers: number;
  revenue: Array<{ currency: string; amount: number }>;
  watchdogs: number;
}
type View = 'upcoming' | 'past' | 'all';
const VIEWS: Array<{ id: View; label: string; hint: string }> = [
  { id: 'upcoming', label: 'Nadcházející', hint: 'Probíhající a budoucí, od nejbližšího' },
  { id: 'past', label: 'Proběhlé', hint: 'Historie, od nejnovějšího' },
  { id: 'all', label: 'Vše', hint: 'Všechny závody, od nejnovějšího' },
];

/** Stav termínu s ohledem na fázi: proběhlý závod není „předběžný“ ani „neznámý“. */
export function dateLabel(r: Pick<RaceListItem, 'phase' | 'dateStatus'>): string {
  if (r.dateStatus === 'cancelled') return label(DATE_STATUS_LABEL, r.dateStatus);
  if (r.phase === 'past') return 'Proběhl';
  if (r.phase === 'ongoing') return 'Probíhá';
  if (r.phase === 'undated') return 'Bez termínu';
  return label(DATE_STATUS_LABEL, r.dateStatus);
}

const revenueText = (s: RaceStats | null): string =>
  !s || s.revenue.length === 0
    ? '—'
    : s.revenue.map((r) => money(r.amount, r.currency === '?' ? 'EUR' : r.currency)).join(' + ');

interface RaceDetail {
  race: RaceListItem & {
    dateEnd: string | null;
    dateVersion: number;
    promiseVersion: number;
    flexiBuyUntil: string | null;
    flexiUseUntil: string | null;
    expectedTicketRelease: string | null;
    racePackUrl: string | null;
    nextRaceId: string | null;
    reviewPolicy: string;
    timezone: string;
    hubCancelledAt: string | null;
  };
  stats: RaceStats | null;
  history: Array<{
    id: number;
    oldStart: string | null;
    newStart: string | null;
    oldStatus: string | null;
    newStatus: string | null;
    dateVersion: number;
    reason: string | null;
    source: string;
    at: string;
  }>;
  tripsByState: Array<{ state: string; n: number }>;
  availability: Array<{ category: string; availability: string; price: string | null }>;
  reviewBatch: { status: string } | null;
  ticketsByCategory: Array<{ category: string | null; ticketStatus: string | null; n: number }>;
}

export function Races({
  me,
  rights,
  param,
}: {
  me: Me;
  rights: Rights;
  param?: string;
}): JSX.Element {
  const [view, setView] = useState<View>('upcoming');
  const [series, setSeries] = useState('');
  const [season, setSeason] = useState('');
  const [country, setCountry] = useState('');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'' | 'asc' | 'desc'>('');
  const q = useDeferredValue(search.trim());
  const list = useQuery<{
    items: RaceListItem[];
    total: number;
    counts: { past: number; upcoming: number; all: number };
    seasons: number[];
    countries: string[];
  }>({
    queryKey: ['races', view, series, season, country, sort, q],
    queryFn: () =>
      api.get(`/api/races${qs({ view, series, season, country, sort, q, pageSize: 200 })}`),
  });

  if (param) return <RaceDetailScreen raceId={param} me={me} rights={rights} />;
  if (list.isLoading) return <LoadingState what="závody" />;
  if (list.isError)
    return (
      <ErrorState
        title="Závody se nepodařilo načíst"
        error={list.error}
        onRetry={() => void list.refetch()}
      />
    );
  const needle = search.trim().toLowerCase();
  const shown = (list.data?.items ?? []).filter(
    (r) =>
      !needle || r.name.toLowerCase().includes(needle) || r.raceId.toLowerCase().includes(needle),
  );

  return (
    <>
      <PageHeader
        title="Závody"
        description="Kalendář, z něhož hub počítá termíny zpráv. Pole, která spravuje e-shop, se tu jen zobrazují a přepíše je další data z e-shopu. Všímejte si závodů s neznámým termínem a chybějícím slibem doručení, kvůli nim se zákazníci ptají nejčastěji."
      />

      <TabBar
        tabs={VIEWS.map((v) => ({
          ...v,
          label: `${v.label}${list.data ? ` (${num(list.data.counts[v.id])})` : ''}`,
        }))}
        value={view}
        onChange={setView}
      />

      <div className="s2a-filters">
        <Field label="Rok / sezóna">
          <select value={season} onChange={(e) => setSeason(e.target.value)}>
            <option value="">Všechny ročníky</option>
            {(list.data?.seasons ?? []).map((y) => (
              <option key={y} value={y}>
                {y > 0 ? y : 'bez ročníku'}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Země">
          <select value={country} onChange={(e) => setCountry(e.target.value)}>
            <option value="">Všechny země</option>
            {(list.data?.countries ?? []).map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Řazení">
          <select value={sort} onChange={(e) => setSort(e.target.value as '' | 'asc' | 'desc')}>
            <option value="">Výchozí</option>
            <option value="asc">Od nejstarších</option>
            <option value="desc">Od nejnovějších</option>
          </select>
        </Field>
        <Field label="Série">
          <select value={series} onChange={(e) => setSeries(e.target.value)}>
            <option value="">Všechny série</option>
            <option value="f1">F1</option>
            <option value="motogp">MotoGP</option>
          </select>
        </Field>
        <Field label="Hledat závod">
          <input
            type="search"
            placeholder="Název nebo kód závodu"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </Field>
      </div>

      <Panel>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Závod</th>
                <th>Termín</th>
                <th>Prodej</th>
                <th className="n">Cest</th>
                <th className="n">Vstupenky</th>
                {view === 'upcoming' ? (
                  <>
                    <th>Slib</th>
                    <th>Podklady k závodu</th>
                    <th>Hodnocení</th>
                    <th>Spravuje</th>
                  </>
                ) : (
                  <>
                    <th className="n">Objednávky</th>
                    <th className="n">Prodáno</th>
                    <th className="n">Tržby</th>
                    <th className="n">Zákazníků</th>
                    <th className="n">Vráceno</th>
                    <th className="n">Hlídačů</th>
                    <th>Hodnocení</th>
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {shown.map((r) => (
                <tr key={r.raceId}>
                  <td>
                    <a href={hrefFor('races', r.raceId)}>{r.name}</a>
                    <div className="muted">
                      <code>{r.raceId}</code>, ročník {r.seasonLabel}
                    </div>
                  </td>
                  <td>
                    {date(r.dateStart, me.defaultTimezone)}
                    <div className="muted">{dateLabel(r)}</div>
                  </td>
                  <td>{label(SALES_STATUS_LABEL, r.effectiveSalesStatus)}</td>
                  <td className="n">
                    {num(r.tripsActive)}
                    <span className="muted"> / {num(r.tripsTotal)}</span>
                  </td>
                  <td className="n">
                    {r.ticketsDeliveredPct === null ? '—' : `${r.ticketsDeliveredPct} %`}
                  </td>
                  {view === 'upcoming' ? (
                    <>
                      <td>
                        {r.promiseBy ? (
                          date(r.promiseBy, me.defaultTimezone)
                        ) : (
                          <StatusBadge tone="warn" icon="alert">
                            Chybí
                          </StatusBadge>
                        )}
                      </td>
                      <td>
                        {r.racePackReady ? (
                          <StatusBadge tone="ok" icon="check">
                            Ano
                          </StatusBadge>
                        ) : (
                          <StatusBadge tone="warn" icon="clock">
                            Ne
                          </StatusBadge>
                        )}
                      </td>
                      <td>{r.reviewStatus ? label(REVIEW_STATUS_LABEL, r.reviewStatus) : '—'}</td>
                      <td>
                        {r.fieldOwner.date === 'hub' ? (
                          <StatusBadge tone="neu" icon="database">
                            Hub
                          </StatusBadge>
                        ) : (
                          <StatusBadge tone="acc" icon="plug">
                            E-shop
                          </StatusBadge>
                        )}
                      </td>
                    </>
                  ) : (
                    <>
                      <td className="n">{num(r.stats?.orders ?? 0)}</td>
                      <td className="n">{num(r.stats?.tickets ?? 0)}</td>
                      <td className="n">{revenueText(r.stats)}</td>
                      <td className="n">{num(r.stats?.customers ?? 0)}</td>
                      <td className="n">
                        {typeof r.stats?.cancelledShare === 'number'
                          ? `${Math.round(r.stats.cancelledShare * 100)} %`
                          : '—'}
                      </td>
                      <td className="n">{num(r.stats?.watchdogs ?? 0)}</td>
                      <td>{r.reviewStatus ? label(REVIEW_STATUS_LABEL, r.reviewStatus) : '—'}</td>
                    </>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {shown.length === 0 ? (
          <EmptyState title={needle || series ? 'Žádný závod neodpovídá' : 'Zatím žádné závody'}>
            {needle || series
              ? 'Zkuste změnit filtr nebo hledaný text.'
              : 'Závody se do hubu dostanou z e-shopu. Zkontrolujte napojení v Kvalitě dat.'}
          </EmptyState>
        ) : null}
      </Panel>
    </>
  );
}

function RaceDetailScreen({
  raceId,
  me,
  rights,
}: {
  raceId: string;
  me: Me;
  rights: Rights;
}): JSX.Element {
  const client = useQueryClient();
  const detail = useQuery<RaceDetail>({
    queryKey: ['race', raceId],
    queryFn: () => api.get(`/api/races/${encodeURIComponent(raceId)}`),
  });
  const plan = useQuery<{
    items: Array<{ code: string; day: string; n: number; merge_only: number }>;
  }>({
    queryKey: ['race-plan', raceId],
    queryFn: () => api.get(`/api/communication/plan${qs({ raceId, days: 30 })}`),
  });
  const [error, setError] = useState<string | null>(null);
  const [ask, setAsk] = useState<'cancel' | 'uncancel' | 'tickets' | null>(null);

  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api.patch(`/api/races/${encodeURIComponent(raceId)}`, body),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['race', raceId] });
      void client.invalidateQueries({ queryKey: ['races'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const cancel = useMutation({
    mutationFn: (v: { what: 'cancel' | 'uncancel'; body?: Record<string, unknown> }) =>
      api.post(`/api/races/${encodeURIComponent(raceId)}/${v.what}`, v.body),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['race', raceId] });
      void client.invalidateQueries({ queryKey: ['races'] });
    },
    onError: (err: Error) => setError(err.message),
  });

  const bulkTickets = useMutation({
    mutationFn: (body: { ticketStatus: string; category?: string }) =>
      api.post(`/api/races/${encodeURIComponent(raceId)}/ticket-status`, body),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['race', raceId] });
    },
    onError: (err: Error) => setError(err.message),
  });

  if (detail.isLoading) return <LoadingState what="závod" />;
  if (detail.isError)
    return (
      <ErrorState
        title="Závod se nepodařilo načíst"
        error={detail.error}
        onRetry={() => void detail.refetch()}
      />
    );
  const data = detail.data;
  if (!data) return <LoadingState />;

  const owner = data.race.fieldOwner;
  const canEdit = rights.write('races');
  const dateEditable = canEdit && owner.date === 'hub';

  return (
    <>
      <a href={hrefFor('races')} className="note">
        ← Závody
      </a>
      <PageHeader
        title={data.race.name}
        description={
          <>
            Kód <code>{data.race.raceId}</code>, ročník {data.race.seasonLabel}, časové pásmo{' '}
            {data.race.timezone}, verze termínu {data.race.dateVersion}.
          </>
        }
      />

      {error ? <ErrorState title="Změnu se nepodařilo uložit" error={new Error(error)} /> : null}

      {data.stats ? (
        <Panel
          title="Souhrn z objednávek"
          subtitle={
            data.race.phase === 'past' ? 'Závod proběhl; čísla z importované historie.' : undefined
          }
        >
          <dl className="kv" data-testid="race-stats">
            <dt>Objednávky</dt>
            <dd>
              {num(data.stats.orders)}
              {data.stats.cancelledShare === null
                ? ''
                : `, vráceno nebo zrušeno ${num(data.stats.ordersCancelled)} (${Math.round(data.stats.cancelledShare * 100)} %)`}
            </dd>
            <dt>Prodané vstupenky</dt>
            <dd>{num(data.stats.tickets)}</dd>
            <dt>Tržby</dt>
            <dd>{revenueText(data.stats)}</dd>
            <dt>Unikátních zákazníků</dt>
            <dd>{num(data.stats.customers)}</dd>
            <dt>Doručené vstupenky</dt>
            <dd>
              {data.race.ticketsDeliveredPct === null ? '—' : `${data.race.ticketsDeliveredPct} %`}
            </dd>
            <dt>Hlídací psi</dt>
            <dd>{num(data.stats.watchdogs)}</dd>
            <dt>Hodnocení zákazníků</dt>
            <dd className="muted">Hub hodnocení nedostává (jen posílá žádosti), proto tu chybí.</dd>
          </dl>
        </Panel>
      ) : null}

      <RacePotential raceId={raceId} canMakeList={rights.write('communication')} />
      <RaceRecommendations raceId={raceId} canMakeList={rights.write('communication')} />

      <RaceLanguages
        raceId={raceId}
        canWrite={rights.write('races')}
        canApprove={rights.write('races')}
      />

      <div className="grid2">
        <Panel title="Termín a prodej">
          {!dateEditable ? (
            <p className="hint">
              Termín, stav prodeje a lhůty Flexi <b>spravuje e-shop</b>, tady jsou jen ke čtení.
              Změní se, až e-shop pošle nová data.
            </p>
          ) : null}
          <dl className="kv">
            <dt>Stav termínu</dt>
            <dd>
              {dateEditable ? (
                <select
                  value={data.race.dateStatus}
                  aria-label="Stav termínu"
                  onChange={(e) => patch.mutate({ dateStatus: e.target.value })}
                >
                  {Object.entries(DATE_STATUS_LABEL).map(([value, text]) => (
                    <option key={value} value={value}>
                      {text}
                    </option>
                  ))}
                </select>
              ) : (
                dateLabel(data.race)
              )}
            </dd>
            <dt>Začátek</dt>
            <dd>{date(data.race.dateStart, data.race.timezone)}</dd>
            <dt>Konec</dt>
            <dd>{date(data.race.dateEnd, data.race.timezone)}</dd>
            <dt>Stav prodeje</dt>
            <dd>
              {canEdit && owner.sales_status === 'hub' ? (
                <select
                  value={data.race.salesStatus}
                  aria-label="Stav prodeje"
                  onChange={(e) => patch.mutate({ salesStatus: e.target.value })}
                >
                  {Object.entries(SALES_STATUS_LABEL).map(([value, text]) => (
                    <option key={value} value={value}>
                      {text}
                    </option>
                  ))}
                </select>
              ) : (
                label(SALES_STATUS_LABEL, data.race.salesStatus)
              )}
            </dd>
            <dt>Flexi: nákup do</dt>
            <dd>{date(data.race.flexiBuyUntil, data.race.timezone)}</dd>
            <dt>Flexi: využití do</dt>
            <dd>{date(data.race.flexiUseUntil, data.race.timezone)}</dd>
          </dl>
        </Panel>

        <Panel title="Komunikační údaje">
          <dl className="kv">
            <dt>Očekávané vydání vstupenek</dt>
            <dd>{date(data.race.expectedTicketRelease, data.race.timezone)}</dd>
            <dt>Slib doručení</dt>
            <dd>
              {data.race.promiseBy ? (
                date(data.race.promiseBy, data.race.timezone)
              ) : (
                <StatusBadge tone="warn" icon="alert">
                  Chybí
                </StatusBadge>
              )}{' '}
              <span className="muted">verze {data.race.promiseVersion}</span>
            </dd>
            <dt>Podklady k závodu (race pack)</dt>
            <dd>
              {data.race.racePackReady ? (
                <StatusBadge tone="ok" icon="check">
                  Připravené
                </StatusBadge>
              ) : (
                <StatusBadge tone="warn" icon="clock">
                  Nepřipravené
                </StatusBadge>
              )}
              {canEdit ? (
                <button
                  className="btn s2a-ml"
                  onClick={() => patch.mutate({ racePackReady: !data.race.racePackReady })}
                >
                  {data.race.racePackReady
                    ? 'Označit jako nepřipravené'
                    : 'Označit jako připravené'}
                </button>
              ) : null}
            </dd>
            <dt>Navazující závod</dt>
            <dd>
              {data.race.nextRaceId ? (
                <a href={hrefFor('races', data.race.nextRaceId)}>
                  <code>{data.race.nextRaceId}</code>
                </a>
              ) : (
                '—'
              )}
            </dd>
            <dt>Politika hodnocení</dt>
            <dd>{data.race.reviewPolicy === 'none' ? 'bez hodnocení' : 'standardní'}</dd>
            <dt>Dávka hodnocení</dt>
            <dd>
              {data.reviewBatch
                ? label(REVIEW_STATUS_LABEL, data.reviewBatch.status)
                : 'ještě nevznikla'}
            </dd>
          </dl>
        </Panel>
      </div>

      <div className="grid2">
        <Panel title="Cesty podle stavu">
          {data.tripsByState.length === 0 ? (
            <EmptyState title="K závodu zatím nejsou cesty">
              Cesty vznikají z objednávek na tento závod.
            </EmptyState>
          ) : (
            <div className="s2a-chart s2a-chart-sm">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={data.tripsByState.map((s) => ({
                    stav: label(TRIP_STATE_LABEL, s.state),
                    n: s.n,
                  }))}
                >
                  <CartesianGrid stroke="var(--grid)" vertical={false} />
                  <XAxis
                    dataKey="stav"
                    stroke="var(--muted)"
                    interval={0}
                    angle={-20}
                    height={60}
                    dy={10}
                  />
                  <YAxis stroke="var(--muted)" allowDecimals={false} />
                  <Tooltip formatter={(v: number) => [num(v), 'cest']} />
                  <Bar dataKey="n" fill="var(--chart1)" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Panel>

        <Panel title="Historie změn termínu">
          {data.history.length === 0 ? (
            <EmptyState title="Termín se nikdy nezměnil">
              Změny termínu a jeho stavu se budou zapisovat sem.
            </EmptyState>
          ) : (
            <div className="scroll">
              <table>
                <thead>
                  <tr>
                    <th>Kdy</th>
                    <th>Z</th>
                    <th>Na</th>
                    <th className="n">Verze</th>
                    <th>Zdroj</th>
                  </tr>
                </thead>
                <tbody>
                  {[...data.history].reverse().map((h) => (
                    <tr key={h.id}>
                      <td>{dateTime(h.at, me.defaultTimezone)}</td>
                      <td>
                        {date(h.oldStart, data.race.timezone)}{' '}
                        <span className="muted">{label(DATE_STATUS_LABEL, h.oldStatus ?? '')}</span>
                      </td>
                      <td>
                        {date(h.newStart, data.race.timezone)}{' '}
                        <span className="muted">{label(DATE_STATUS_LABEL, h.newStatus ?? '')}</span>
                      </td>
                      <td className="n">{h.dateVersion}</td>
                      <td>{h.source}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Panel>
      </div>

      {owner.date === 'hub' && canEdit ? (
        <Panel title="Hromadný stav vstupenek">
          <p className="note">
            Jen u závodu, který spravuje hub. Po změně se přepočítají cesty a e-shop může zákazníkům
            poslat zprávu o doručení vstupenek.
          </p>
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Kategorie</th>
                  <th>Stav</th>
                  <th className="n">Položek</th>
                </tr>
              </thead>
              <tbody>
                {data.ticketsByCategory.map((t, i) => (
                  <tr key={`${t.category}-${t.ticketStatus}-${i}`}>
                    <td>{t.category ?? '—'}</td>
                    <td>{label(TICKET_STATUS_LABEL, t.ticketStatus ?? '')}</td>
                    <td className="n">{num(t.n)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="row s2a-mt">
            <button className="btn" onClick={() => setAsk('tickets')}>
              Označit všechny vstupenky jako doručené
            </button>
          </div>
        </Panel>
      ) : null}

      {canEdit ? (
        <Panel title="Zrušení závodu">
          <p className="hint">
            Zrušení zadává správce ručně. Data z e-shopu ho nepřepíšou, dokud ho nevrátíte.
          </p>
          <div className="row">
            {data.race.hubCancelledAt ? (
              <button
                className="btn"
                disabled={cancel.isPending}
                onClick={() => setAsk('uncancel')}
              >
                Vrátit zrušení
              </button>
            ) : (
              <button
                className="btn dng"
                disabled={cancel.isPending}
                onClick={() => setAsk('cancel')}
              >
                Zrušit závod
              </button>
            )}
          </div>
        </Panel>
      ) : null}

      <Panel title="Plán komunikace k závodu (30 dní)">
        {plan.isLoading ? (
          <LoadingState what="plán" />
        ) : plan.isError ? (
          <ErrorState
            title="Plán komunikace se nepodařilo načíst"
            error={plan.error}
            onRetry={() => void plan.refetch()}
          />
        ) : (plan.data?.items.length ?? 0) === 0 ? (
          <EmptyState title="Nic na řadě">
            V příštích 30 dnech k tomuto závodu neodejde žádná zpráva.
          </EmptyState>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Den</th>
                  <th>Kód</th>
                  <th className="n">Počet</th>
                  <th className="n">Z toho jen ke sloučení</th>
                </tr>
              </thead>
              <tbody>
                {plan.data?.items.map((p, i) => (
                  <tr key={`${p.day}-${p.code}-${i}`}>
                    <td>{date(p.day, me.defaultTimezone)}</td>
                    <td>
                      <code>{p.code}</code>
                    </td>
                    <td className="n">{num(p.n)}</td>
                    <td className="n">{num(p.merge_only)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      <ConfirmDialog
        open={ask === 'cancel'}
        title="Zrušit závod?"
        danger
        confirmLabel="Zrušit závod"
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          cancel.mutate({ what: 'cancel' });
          setAsk(null);
        }}
      >
        Závod se označí jako zrušený a zákazníkům s cestou odejde zpráva o zrušení. Data z e-shopu
        to nepřepíšou.
      </ConfirmDialog>
      <ConfirmDialog
        open={ask === 'uncancel'}
        title="Vrátit zrušení závodu?"
        confirmLabel="Vrátit zrušení"
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          cancel.mutate({ what: 'uncancel', body: { dateStatus: 'unknown' } });
          setAsk(null);
        }}
      >
        Termín pak znovu určí data z e-shopu.
      </ConfirmDialog>
      <ConfirmDialog
        open={ask === 'tickets'}
        title="Označit vstupenky jako doručené?"
        confirmLabel="Označit jako doručené"
        onCancel={() => setAsk(null)}
        onConfirm={() => {
          bulkTickets.mutate({ ticketStatus: 'delivered' });
          setAsk(null);
        }}
      >
        Všechny aktivní vstupenky závodu se označí jako doručené a přepočítají se cesty.
      </ConfirmDialog>
    </>
  );
}
